import { type FormEvent, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ApiError, errorMessage, UserFacingError } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { PasswordInput } from '@/shared/ui/PasswordInput';

import { elevationApi } from './api';
import { ElevationContext, type ElevationValue } from './context';

interface Pending {
  resolve: () => void;
  reject: (reason: Error) => void;
}

/**
 * The password prompt of the elevated mode (contract v10), mounted once above the app.
 *
 * Kept as a promise so the caller reads like the action it wraps: `await
 * withElevation(() => adminApi.createUser(payload))`. The prompt appears only when the
 * API says it is needed, which keeps the front out of the business of guessing whether
 * five minutes have passed.
 */
export function ElevationProvider({ children }: { children: ReactNode }) {
  const [elevatedUntil, setElevatedUntil] = useState<Date | null>(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef<Pending | null>(null);

  const ensureElevated = useCallback((): Promise<void> => {
    if (elevatedUntil && elevatedUntil.getTime() > Date.now()) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      pending.current = { resolve, reject };
      setPassword('');
      setError(null);
      setOpen(true);
    });
  }, [elevatedUntil]);

  const withElevation = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    try {
      return await action();
    } catch (caught) {
      if (!(caught instanceof ApiError) || !caught.needsElevation) throw caught;
      // The API said the session is not elevated: whatever we remembered is stale.
      setElevatedUntil(null);
      await new Promise<void>((resolve, reject) => {
        pending.current = { resolve, reject };
        setPassword('');
        setError(null);
        setOpen(true);
      });
      return await action();
    }
  }, []);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void confirm();
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const result = await elevationApi.elevate(password);
      setElevatedUntil(result.elevated_until ? new Date(result.elevated_until) : null);
      setOpen(false);
      setPassword('');
      pending.current?.resolve();
      pending.current = null;
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setOpen(false);
    setPassword('');
    pending.current?.reject(
      new UserFacingError('Cancelado: hacía falta volver a escribir la contraseña.'),
    );
    pending.current = null;
  }

  const value = useMemo<ElevationValue>(
    () => ({ elevatedUntil, ensureElevated, withElevation }),
    [elevatedUntil, ensureElevated, withElevation],
  );

  // Painted on `body` and above any `Dialog` (z-60 over its z-50): the action that needs
  // the password may have been launched from inside a sheet («Borrar definitivamente»),
  // and a prompt that paints behind that sheet's backdrop leaves the button in
  // «Borrando…» for ever, with nothing to type into. A portal appended after the sheet
  // and one step higher wins on both counts.
  const prompt = open
    ? createPortal(
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/40 p-4"
          role="presentation"
        >
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="elevation-title"
            onSubmit={submit}
            className="bg-card border-line flex w-full max-w-md flex-col gap-4 rounded-lg border p-6 shadow-lg"
          >
            <div className="flex flex-col gap-1">
              <h2 id="elevation-title" className="font-display text-ink text-[20px] font-semibold">
                Vuelve a escribir tu contraseña
              </h2>
              <p className="text-ink-3 text-[13.5px]">
                Lo que vas a hacer cambia quién entra o qué hace el sistema con el correo. Se pide
                otra vez la contraseña y queda anotado. Vale cinco minutos.
              </p>
            </div>
            <PasswordInput
              label="Contraseña"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={error ?? undefined}
              required
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={cancel} disabled={busy}>
                Cancelar
              </Button>
              <Button type="submit" disabled={busy || password.length < 8}>
                {busy ? 'Comprobando…' : 'Continuar'}
              </Button>
            </div>
          </form>
        </div>,
        document.body,
      )
    : null;

  return (
    <ElevationContext.Provider value={value}>
      {children}
      {prompt}
    </ElevationContext.Provider>
  );
}
